import { injectable } from "inversify";
import type { IpfModule } from ".";
import { Constants } from "./constants.generated";
import { onLoanStatusBuildRowLinkTemplate } from "./functions/on-loan-status-build-row-link-template";
import { onBrokerReportGridBuildRowLinkTemplate } from "./functions/on-brokerreport-grid-build-row-link-template";
import { container } from "./ioc/container.config";
import { TYPES } from "./ioc/types";
import { ClientContext } from "./models/app";
import { PageInterface } from "./pages";
import { PageService, SessionValidationService } from "./services";
import { ServiceBase } from "./services/ServiceBase";

@injectable()
export class IpfApplication implements IpfModule {

	private _context = {} as ClientContext;
	private _page?: PageInterface;
	private _ensuredActiveElement: HTMLElement | null = null;
	private readonly _onReady = new Array<() => void>();
	private readonly _data = {} as { [key: string]: any; };

	public get onReady(): Array<() => void> {
		return this._onReady;
	}

	public get context(): ClientContext {
		return this._context;
	}

	public get page() {
		return this._page;
	}

	public get handlers() {
		const res: Record<string, Function> = {};
		res[Constants.LoanStatusBuildRowLinkTemplateFunction] = onLoanStatusBuildRowLinkTemplate;
		res[Constants.BrokerReportGridBuildRowLinkTemplateFunction] = onBrokerReportGridBuildRowLinkTemplate;
		return res;
	}

	public get ensuredActiveElement(): HTMLElement | null {
		return this._ensuredActiveElement;
	}

	public set ensuredActiveElement(element: HTMLElement | null) {
		this._ensuredActiveElement = element;
	}

	public addOnReady(handler: () => void) {

		this.onReady.push(handler);
	}

	public applyEffects(element: Element) {

		this.getService<PageService>(TYPES.PageService).applyEffects(element);
	}

	public clearData(key: string) {

		delete this._data[key];
	}

	public getService<TService extends ServiceBase>(key: string): TService {

		return container.get<TService>(key);
	}

	public getData<TData>(key: string): TData {
		return this._data[key] as TData;
	}

	public registerPage<TPage extends PageInterface>(service: { new(...args: any[]): TPage; }, id: string) {
		container.bind<TPage>(id).to(service);
		this._page = container.get<TPage>(id);
		return this._page as TPage;
	}

	public setData<TData>(key: string, data: TData) {
		this._data[key] = data;
	}

	public setContext(context: ClientContext) {
		this._context = context;
		container.bind<ClientContext>(TYPES.ClientContext).toConstantValue(context);

		container.get<SessionValidationService>(TYPES.SessionValidationService).setupSessionTimer();
	}

	public refreshSessionTimeout() {
		this.getService<SessionValidationService>(TYPES.SessionValidationService).refreshSessionTimer();
	}
}